import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';
import { AxiosError } from 'axios';

import { useAlertsStore } from '@tallo/design-system';

import { CreatePortfolioDto, PortfolioDto, UpdatePortfolioDto } from '../interfaces/portfolio.dto';
import { companyPortfoliosQueryKey } from '../queries/company-portfolios.query';
import portfolioService from '../services/portfolio.service';

/**
 * Every portfolio change refreshes the company's list; the caller says what else it touched (the
 * properties page reads each unit's portfolio, the Team tab its collaborators). The change itself
 * already happened, so a failed refresh is not an error.
 */
function refreshPortfolios() {
  useQueryCache()
    .invalidateQueries({ key: [companyPortfoliosQueryKey] })
    .catch(() => undefined);
}

function reportError(error: unknown) {
  // A rejected field is shown on the form that sent it
  if (error instanceof AxiosError && error.response?.data?.validationErrors) {
    return;
  }

  useAlertsStore().catchError(error);
}

export const useCreatePortfolio = defineMutation(() => {
  return useMutation({
    mutation: (dto: CreatePortfolioDto) => portfolioService.create(dto),
    onSuccess: refreshPortfolios,
    onError: reportError,
  });
});

/** A rename, a listing contact, a hand-over, or several at once */
export const useUpdatePortfolio = defineMutation(() => {
  return useMutation({
    mutation: (payload: { portfolioId: string; changes: UpdatePortfolioDto }) =>
      portfolioService.update(payload.portfolioId, payload.changes),
    onSuccess: refreshPortfolios,
    onError: reportError,
  });
});

export const useAddPortfolioCollaborator = defineMutation(() => {
  return useMutation({
    mutation: (payload: { portfolioId: string; investorId: string }) =>
      portfolioService.addCollaborator(payload.portfolioId, payload.investorId),
    onSuccess: refreshPortfolios,
    onError: reportError,
  });
});

export const useRemovePortfolioCollaborator = defineMutation(() => {
  return useMutation({
    mutation: (payload: { portfolioId: string; investorId: string }) =>
      portfolioService.removeCollaborator(payload.portfolioId, payload.investorId),
    onSuccess: refreshPortfolios,
    onError: reportError,
  });
});

export const useDeletePortfolio = defineMutation(() => {
  return useMutation({
    mutation: (portfolio: PortfolioDto) => portfolioService.remove(portfolio.id),
    onSuccess: refreshPortfolios,
    onError: reportError,
  });
});

// No toast belongs here: the list already shows the new order, and one per drag would be noise
export const useReorderPortfolios = defineMutation(() => {
  return useMutation({
    mutation: (portfolioIds: string[]) => portfolioService.reorder(portfolioIds),
    // Re-read either way: on success to confirm, on failure to undo the order shown locally
    onSettled: refreshPortfolios,
    onError: reportError,
  });
});
